import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enCommon from './locales/en/common.json';
import enJoinPage from './locales/en/joinPage.json';
import enMePage from './locales/en/mePage.json';
import enInbox from './locales/en/inbox.json';
// French translations sourced from Claude Opus 4.7 baseline (8 Jun
// 2026). Structural UI strings only — short, non-voice-bearing.
// Persona content (Morwenna's Kernewek phrasings, partner voice
// guidelines) lives elsewhere and is handled separately in Phase 4c.
// Awaiting native-speaker review before the French market launch
// announcement; until then, French is live for guests who explicitly
// select it OR whose browser language matches. Behaviour-correct;
// translation-quality TBD.
import frCommon from './locales/fr/common.json';
import frJoinPage from './locales/fr/joinPage.json';
import frMePage from './locales/fr/mePage.json';
import frInbox from './locales/fr/inbox.json';
// German translations sourced from Claude Opus 4.7 baseline (9 Jun
// 2026, Phase 4e). Structural UI strings only — same scope as the
// French baseline above. Register: formal "Sie" throughout, matching
// German hospitality convention and the formal "vous" used in the
// French baseline. Awaiting native-speaker review before the German
// market launch announcement; until then, German is live for guests
// who explicitly select it OR whose browser language matches.
// Behaviour-correct; translation-quality TBD.
import deCommon from './locales/de/common.json';
import deJoinPage from './locales/de/joinPage.json';
import deMePage from './locales/de/mePage.json';
import deInbox from './locales/de/inbox.json';
// Spanish translations sourced from Claude Opus 4.7 baseline (9 Jun
// 2026, Phase 4e). Structural UI strings only — same scope as the
// French baseline above. Register: informal "tú" throughout. This is
// a deliberate divergence from the French formal "vous" — modern
// Spanish hospitality and travel UX overwhelmingly uses "tú" for
// warmth, and "usted" reads as distant for a friend-of-the-locals
// product like TownInsider. Cross-region applicable (works in both
// peninsular and Latin American Spanish). Awaiting native-speaker
// review before the Spanish market launch announcement; until then,
// Spanish is live for guests who explicitly select it OR whose
// browser language matches. Behaviour-correct; translation-quality
// TBD.
import esCommon from './locales/es/common.json';
import esJoinPage from './locales/es/joinPage.json';
import esMePage from './locales/es/mePage.json';
import esInbox from './locales/es/inbox.json';
// installNudge namespace — Phase 6 sub-commit 7 (13 Jun 2026). Powers
// the PWA install banner on MePage. FR/DE/ES translations sourced
// from the same Claude Opus 4.7 baseline as the existing namespaces;
// behaviour-correct, awaiting native-speaker review before each
// market launch. Same register conventions: FR formal "vous", DE
// formal "Sie", ES informal "tú".
import enInstallNudge from './locales/en/installNudge.json';
import frInstallNudge from './locales/fr/installNudge.json';
import deInstallNudge from './locales/de/installNudge.json';
import esInstallNudge from './locales/es/installNudge.json';

/**
 * i18next configuration for towninsider-app — Phase 4a infrastructure
 * (8 Jun 2026).
 *
 * Initialises a single i18next instance with:
 *   - English bundled at module load (no async wait before first render)
 *   - Browser language detection with localStorage persistence
 *   - Strict fallback to English when a requested key is missing
 *   - React bindings via react-i18next
 *
 * Namespace strategy:
 *   - `common` — strings shared across multiple surfaces (e.g. button
 *     labels, validation messages, language names). Default namespace.
 *   - `<pageName>` — strings used only by one page (e.g. `joinPage`,
 *     `mePage`, `chatPage`). Loaded lazily per-page in later phases
 *     so each page only pays for the strings it renders.
 *
 * Phase 4a-1 shipped only the `common` namespace. Phase 4a-2a
 * registered `joinPage`. Phase 4b-1 bundled French translations of
 * both namespaces statically alongside English. Phase 4b-4 registered
 * `mePage` — the main app surface guests spend the bulk of their
 * stay on. Persona-sourced strings (welcome, onboarding prompts,
 * ack, teatime nudge, error replies) are NOT in mePage; they live
 * on persona records in personas.ts and are internationalised
 * separately in Phase 4c via persona schema extension. Phase 4e
 * (9 Jun 2026) bundled German and Spanish — both AI-baseline, both
 * behaviour-correct, both awaiting native-speaker review before
 * market-launch announcements (see locale-specific import comments
 * above). Phase 5c (13 Jun 2026, this revision) registers `inbox`
 * — the guest PWA Updates surface populated by GET /v1/announcements.
 * The bell-icon aria-label keys live in this namespace too (consumed
 * from MePage via a second useTranslation('inbox') call) rather
 * than polluting mePage.json with cross-feature keys. Phase 6
 * sub-commit 7 (13 Jun 2026) registers `installNudge` — the
 * dismissible PWA install banner shown above ChatInput on MePage,
 * with platform-aware behaviour (native prompt on Android/Chromium,
 * Share → Add to Home Screen instructions modal on iOS Safari).
 *
 * Language detection order (`detection.order`):
 *   1. localStorage (key: 'towninsider.language') — the partner's
 *      previously-saved choice wins over browser default. This is
 *      the durable preference, set by the language selector once
 *      that UI lands in Phase 4b.
 *   2. navigator — fall back to the browser's primary language
 *      (`navigator.language`). Honours the guest's OS-level pref
 *      on first visit.
 *
 * `caches: ['localStorage']` writes the detected language back to
 * localStorage on every set, so cross-session persistence works
 * automatically without code at the page level.
 *
 * Fallback chain:
 *   - Missing key in current language → English fallback
 *   - Missing key in English → returns the key itself (i18next
 *     default behaviour). Type-safe key inference via
 *     `src/types/i18next.d.ts` (added in 4a-2a alongside this update)
 *     means a missing key fails at compile time, not runtime.
 *
 * `interpolation.escapeValue: false` — React already escapes
 * interpolated values, so disabling i18next's HTML escaping
 * avoids double-escaping (`&amp;amp;`).
 *
 * `returnNull: false` — narrows the `t()` return type to `string`
 * instead of `string | null`, removing a defensive null-check at
 * every call site. The trade-off is that a missing key returns the
 * key string itself rather than null — acceptable because type
 * safety prevents missing keys reaching production.
 *
 * `void` operator on `.init(...)` — i18next.init returns a Promise
 * but we deliberately don't await it at module load. The bundled
 * English namespaces are synchronously available; async languages
 * trigger re-renders via react-i18next when they finish loading.
 * The `void` operator documents that we know it's a Promise and
 * are intentionally not awaiting (silences strict no-floating-
 * promises if enabled later).
 */

/** Default namespace for the app. Most cross-page strings live here;
 *  per-page namespaces (e.g. `joinPage`) are added when a page-specific
 *  string doesn't belong in `common`. */
export const DEFAULT_NAMESPACE = 'common';

/** The set of languages this PWA supports — locked early so type
 *  checks against `SupportedLanguage` work from day one. Adding a
 *  language is a one-line edit here plus a new locales/<lang>/
 *  folder. */
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'de', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** localStorage key for the partner's persisted language choice.
 *  Namespaced under `towninsider.` so the dashboard / partner repo
 *  can use parallel keys without collision in shared origins. */
export const LANGUAGE_STORAGE_KEY = 'towninsider.language';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    defaultNS: DEFAULT_NAMESPACE,
    ns: [DEFAULT_NAMESPACE, 'joinPage', 'mePage', 'inbox', 'installNudge'],
    resources: {
      en: {
        common: enCommon,
        joinPage: enJoinPage,
        mePage: enMePage,
        inbox: enInbox,
        installNudge: enInstallNudge,
      },
      fr: {
        common: frCommon,
        joinPage: frJoinPage,
        mePage: frMePage,
        inbox: frInbox,
        installNudge: frInstallNudge,
      },
      de: {
        common: deCommon,
        joinPage: deJoinPage,
        mePage: deMePage,
        inbox: deInbox,
        installNudge: deInstallNudge,
      },
      es: {
        common: esCommon,
        joinPage: esJoinPage,
        mePage: esMePage,
        inbox: esInbox,
        installNudge: esInstallNudge,
      },
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes interpolated values; disabling
      // i18next's own escaping avoids double-escape on safe content.
      escapeValue: false,
    },
    // Narrows t() return type to `string` instead of `string | null`.
    // Missing keys return the key itself, surfaced at compile time
    // by the types augmentation in `src/types/i18next.d.ts`.
    returnNull: false,
  });

export default i18n;
